import { createFileRoute } from '@tanstack/react-router';
import { warm, warmBackground } from '@/api/loaders';
import { reportingQuery, reportingQueryAllPages } from '@/api/resources';
import { requireCapability } from '@/lib/route-guards';
import {
  parsePerformanceReportSearch,
  serializePerformanceReportSearch,
  updatePerformanceReportSearch,
} from '@/lib/performance-report-search';
import type { PerformanceReportSearchPatch } from '@/lib/performance-report-search';
import {
  buildPerformanceReportFilters,
  buildPerformanceReportRequest,
  PERFORMANCE_TAB_GROUPS,
} from '@/lib/performance-report-request';
import { PerformanceReport } from '@/screens/performance-report';

export const Route = createFileRoute('/reports/')({
  beforeLoad: requireCapability('reports_view'),
  validateSearch: parsePerformanceReportSearch,
  loaderDeps: ({ search }) => search,
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  loader: ({ context, deps }) => {
    const filters = buildPerformanceReportFilters(deps);
    const unfilteredOptions = buildPerformanceReportFilters({
      ...deps,
      productionId: undefined,
      campaignId: undefined,
      target: undefined,
      channel: undefined,
      audienceId: undefined,
      audienceRevision: undefined,
      audienceType: undefined,
      creativeId: undefined,
    });
    const trend = buildPerformanceReportRequest(deps, ['day'], filters, 500, false);
    const active = buildPerformanceReportRequest(
      deps,
      PERFORMANCE_TAB_GROUPS[deps.tab],
      filters,
    );
    warmBackground(
      context.queryClient,
      reportingQueryAllPages(buildPerformanceReportRequest(deps, ['campaign'], unfilteredOptions, 500, false)),
      reportingQueryAllPages(buildPerformanceReportRequest(deps, ['assigned_production'], unfilteredOptions, 500, false)),
      reportingQueryAllPages(buildPerformanceReportRequest(deps, ['audience'], unfilteredOptions, 500, false)),
      reportingQueryAllPages(buildPerformanceReportRequest(deps, ['creative'], unfilteredOptions, 500, false)),
      ...(deps.tab === 'overview'
        ? [reportingQuery(buildPerformanceReportRequest(deps, ['channel'], filters, 500, false))]
        : []),
    );
    return warm(
      context.queryClient,
      reportingQuery(trend),
      reportingQueryAllPages(active),
    );
  },
  component: Page,
});

function Page() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <PerformanceReport
      search={search}
      onSearchChange={(
        patch: PerformanceReportSearchPatch,
        options?: { replace?: boolean },
      ) => {
        void navigate({
          // The complete validated state is written on every navigation. Tabs
          // push by default for back/forward history; high-frequency controls
          // may opt into replace without losing any other search dimension.
          search: (current) => serializePerformanceReportSearch(
            updatePerformanceReportSearch(current, patch),
          ),
          replace: options?.replace,
        });
      }}
    />
  );
}
